import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  SignalIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  AI_CAPTURE_FLOOR_DEFAULT,
  DEFAULT_TAG_PROMPT_TEMPLATE,
  type AiActionMode,
  type AiModel,
  type AiProbeRequest,
  type AiProvider,
  type AiProviderKind,
  type AiVisionTestResult,
} from '@xphoto/shared';
import { useI18n } from '../i18n';
import { recordAction } from '../diagnostics/buffers';
import { AppConfigApiError, updateAppSettings, useAppConfig } from '../app-config';
import {
  AiApiError,
  createAiProvider,
  discoverAiModels,
  fetchAiProviders,
  testAiConnection,
  testAiVision,
  updateAiProvider,
} from './ai-api';
import {
  ACTION_MODE_LABEL,
  ACTION_MODE_ORDER,
  DEFAULT_API_KEY_ENV,
  DEFAULT_BASE_URL,
  PROVIDER_DOC_URL,
  PROVIDER_LABEL,
  PROVIDER_NOTE_KEY,
  PROVIDER_ORDER,
  REQUIRES_API_KEY,
} from './provider-meta';

/**
 * Section « CONFIGURATION LLM » (T5 lot A, XPHOTO-26).
 *
 * SECTION de la console SUPERADMIN (`/superadmin/llm`), réservée au SUPERADMIN
 * (gating à la route `AdminRoute`, portée `system`). Rendue comme les autres sections
 * admin (`role="tabpanel"`, `<h3>`) dans le corps défilant fourni par `AdminPanel` —
 * elle ne porte donc PAS de propre en-tête de page, de bouton de fermeture ni de
 * conteneur de défilement (le fil d'Ariane « Accueil / Superadmin / Configuration LLM »
 * + la barre d'onglets sont fournis par l'`AppChrome`/`AdminPanel`).
 *
 * Configure un fournisseur de vision (LM Studio local, Claude, Gemini, Grok) :
 * fournisseur, URL de base, modèle (saisie libre + LISTE CLIQUABLE peuplée après un
 * test réussi), mode d'action IA, badge « Clé API détectée » (via la variable d'env,
 * sans jamais afficher la clé), « Tester la connexion » et « Enregistrer et activer ».
 *
 * ⚠ SÉCURITÉ (ADR-0002 §E) : aucune clé n'est saisie ni affichée — seul le NOM de
 * sa variable d'env. Le serveur renvoie `apiKeyPresent` (booléen).
 */
interface LlmConfigPageProps {
  libraryId: string;
}

/** Champ de saisie commun (aligné sur les autres écrans). */
const inputClass =
  'h-11 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
const selectClass = inputClass;

/** État courant du formulaire (fournisseur en cours d'édition). */
interface FormState {
  kind: AiProviderKind;
  baseUrl: string;
  apiKeyEnv: string;
  model: string;
  actionMode: AiActionMode;
  /** Plancher de capture, en FRACTION 0..1 (l'UI l'affiche en pourcent). */
  captureFloor: number;
  /** Template de prompt d'indexation (EN ANGLAIS) ; `''` = template par défaut. */
  promptTemplate: string;
}

function initialForm(): FormState {
  return {
    kind: 'openai_compat',
    baseUrl: DEFAULT_BASE_URL.openai_compat,
    apiKeyEnv: DEFAULT_API_KEY_ENV.openai_compat,
    model: '',
    actionMode: 'auto',
    captureFloor: AI_CAPTURE_FLOOR_DEFAULT,
    promptTemplate: '',
  };
}

/** Reconstruit l'état du formulaire à partir d'un fournisseur enregistré (hydratation). */
function formFromProvider(p: AiProvider): FormState {
  return {
    kind: p.kind,
    baseUrl: p.baseUrl,
    apiKeyEnv: p.apiKeyEnv,
    model: p.model,
    actionMode: p.actionMode,
    captureFloor: p.captureFloor,
    promptTemplate: p.promptTemplate ?? '',
  };
}

/** Égalité champ à champ (détection « modifications non enregistrées »). */
function formEqual(a: FormState, b: FormState): boolean {
  return (
    a.kind === b.kind &&
    a.baseUrl === b.baseUrl &&
    a.apiKeyEnv === b.apiKeyEnv &&
    a.model === b.model &&
    a.actionMode === b.actionMode &&
    a.captureFloor === b.captureFloor &&
    a.promptTemplate === b.promptTemplate
  );
}

/** Bornes du plancher affiché en pourcent (0..100). */
function clampPercent(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

type TestState =
  | { status: 'idle' }
  | { status: 'testing' }
  | {
      status: 'done';
      reachable: boolean;
      apiKeyPresent: boolean;
      modelCount: number;
      error: string | null;
      /**
       * Résultat du DOUBLE TEST du modèle (répond au texte + accepte une image), ou
       * `null` si aucun modèle n'était saisi au moment du test (vision non testée).
       */
      vision: AiVisionTestResult | null;
    };

export function LlmConfigPage({ libraryId }: LlmConfigPageProps): React.JSX.Element {
  const { t } = useI18n();

  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [form, setForm] = useState<FormState>(initialForm);
  /**
   * Référence « propre » = dernières valeurs ENREGISTRÉES/CHARGÉES. Sert à détecter
   * les modifications non enregistrées (`dirty`) et à revenir au style neutre après
   * un enregistrement réussi.
   */
  const [baseline, setBaseline] = useState<FormState>(initialForm);
  const [models, setModels] = useState<AiModel[]>([]);
  const [test, setTest] = useState<TestState>({ status: 'idle' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Échec du chargement INITIAL des fournisseurs (audit F10). Avant, un `.catch(() => {})`
   * rendait l'écran « Configuration LLM » VIDE ET SANS EXPLICATION quand le backend
   * échouait — indiscernable d'une installation neuve sans fournisseur.
   */
  const [loadError, setLoadError] = useState(false);

  /**
   * L'hydratation du formulaire depuis le fournisseur actif ne doit se faire QU'UNE
   * FOIS (premier chargement) : un re-fetch ultérieur (après enregistrement) ne doit
   * jamais écraser une édition en cours. Ce drapeau (ref, hors cycle de rendu) garde
   * la trace.
   */
  const hydratedRef = useRef(false);

  // Charge les fournisseurs déjà configurés + hydrate le formulaire depuis l'actif.
  useEffect(() => {
    const controller = new AbortController();
    setLoadError(false);
    fetchAiProviders(libraryId, controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        setProviders(list);
        // Hydrate UNE seule fois, depuis le fournisseur actif (défaut activé).
        if (!hydratedRef.current) {
          const active = list.find((p) => p.isDefault && p.enabled) ?? null;
          if (active) {
            const hydrated = formFromProvider(active);
            setForm(hydrated);
            setBaseline(hydrated);
          }
          hydratedRef.current = true;
        }
      })
      .catch(() => {
        // La liste est indisponible : l'écran reste UTILISABLE pour créer un
        // fournisseur, mais on le DIT (sinon « vide » et « en panne » se confondent).
        if (controller.signal.aborted) return;
        setLoadError(true);
      });
    return () => controller.abort();
  }, [libraryId]);

  /** Fournisseur actif (défaut activé) de la portée, s'il existe. */
  const activeProvider = useMemo(
    () => providers.find((p) => p.isDefault && p.enabled) ?? null,
    [providers],
  );

  /** Modifications non enregistrées : le formulaire diffère de la référence propre. */
  const dirty = useMemo(() => !formEqual(form, baseline), [form, baseline]);

  // Dès qu'une modification non enregistrée apparaît, on efface la confirmation
  // « enregistré » (elle ne vaut plus pour l'état courant).
  useEffect(() => {
    if (dirty) setSaved(false);
  }, [dirty]);

  const requiresKey = REQUIRES_API_KEY[form.kind];

  /** Change de fournisseur : réinitialise URL/env/modes découverts au défaut du kind. */
  const onChangeKind = useCallback((kind: AiProviderKind) => {
    setForm((f) => ({
      ...f,
      kind,
      baseUrl: DEFAULT_BASE_URL[kind],
      apiKeyEnv: DEFAULT_API_KEY_ENV[kind],
    }));
    setModels([]);
    setTest({ status: 'idle' });
    setSaved(false);
    setError(null);
  }, []);

  /**
   * Modifie un champ pertinent pour la CONNEXION (`baseUrl`/`apiKeyEnv`/`model`) et
   * réinitialise l'état du test : un « vert » périmé ne doit jamais subsister après
   * un changement de paramètres. Efface aussi la confirmation d'enregistrement.
   */
  const onChangeConnField = useCallback((patch: Partial<FormState>) => {
    setForm((f) => ({ ...f, ...patch }));
    setTest({ status: 'idle' });
    setSaved(false);
  }, []);

  const probeBody = useCallback(
    (): AiProbeRequest => ({
      kind: form.kind,
      baseUrl: form.baseUrl,
      apiKeyEnv: form.apiKeyEnv,
      model: form.model || undefined,
    }),
    [form],
  );

  /**
   * Teste la connexion + découvre les modèles + (si un modèle est saisi) DOUBLE TEST
   * du modèle : (A) il répond à une invite texte, (B) il accepte une mini-image. Ainsi
   * « Connexion OK » ne signale plus faussement un modèle texte-seul incapable de vision.
   */
  const onTest = useCallback(async () => {
    setTest({ status: 'testing' });
    setError(null);
    setSaved(false);
    const model = form.model.trim();
    try {
      const [result, discovered, vision] = await Promise.all([
        testAiConnection(libraryId, probeBody()),
        discoverAiModels(libraryId, probeBody()),
        model !== ''
          ? testAiVision(libraryId, {
              kind: form.kind,
              baseUrl: form.baseUrl,
              apiKeyEnv: form.apiKeyEnv,
              model,
            })
          : Promise.resolve(null),
      ]);
      recordAction('llm.test');
      setModels(discovered.models);
      setTest({
        status: 'done',
        reachable: result.reachable,
        apiKeyPresent: result.apiKeyPresent,
        modelCount: result.modelCount,
        error: result.error,
        vision,
      });
    } catch (err: unknown) {
      if (err instanceof AiApiError && err.status === 403) {
        setError(t('llm.errorForbidden'));
      } else {
        setError(t('llm.errorGeneric'));
      }
      setTest({ status: 'idle' });
    }
  }, [libraryId, probeBody, form.kind, form.baseUrl, form.apiKeyEnv, form.model, t]);

  /**
   * Enregistre le fournisseur ET l'active (isDefault + enabled). MET À JOUR le
   * fournisseur actif existant (PATCH) s'il y en a un — sinon en CRÉE un (POST) —
   * afin de ne jamais dupliquer un fournisseur à chaque enregistrement. Après succès,
   * re-hydrate la référence propre (le formulaire n'est plus « modifié » → persiste au
   * rechargement).
   */
  const onSave = useCallback(async () => {
    if (saving) return;
    setError(null);
    setSaved(false);
    if (form.model.trim() === '') {
      setError(t('llm.errorModelRequired'));
      return;
    }
    if (form.baseUrl.trim() === '') {
      setError(t('llm.errorBaseUrlRequired'));
      return;
    }
    // Instantané normalisé (valeurs effectivement envoyées → référence propre).
    const snapshot: FormState = {
      kind: form.kind,
      baseUrl: form.baseUrl.trim(),
      apiKeyEnv: form.apiKeyEnv.trim(),
      model: form.model.trim(),
      actionMode: form.actionMode,
      captureFloor: form.captureFloor,
      promptTemplate: form.promptTemplate,
    };
    const payload = {
      // Nom lisible = libellé du fournisseur (l'écran ne demande pas de nom libre).
      name: t(PROVIDER_LABEL[snapshot.kind]),
      kind: snapshot.kind,
      baseUrl: snapshot.baseUrl,
      apiKeyEnv: snapshot.apiKeyEnv,
      model: snapshot.model,
      actionMode: snapshot.actionMode,
      captureFloor: snapshot.captureFloor,
      // Chaîne vide/blanche → `null` (= prompt par défaut ; le serveur re-normalise aussi).
      promptTemplate: snapshot.promptTemplate.trim() === '' ? null : snapshot.promptTemplate,
      isDefault: true,
      enabled: true,
    };
    setSaving(true);
    try {
      if (activeProvider) {
        await updateAiProvider(libraryId, activeProvider.id, payload);
      } else {
        await createAiProvider(libraryId, payload);
      }
      recordAction('llm.save');
      // Rafraîchit la liste ; le fournisseur enregistré est l'unique actif.
      const refreshed = await fetchAiProviders(libraryId);
      setProviders(refreshed);
      // Ré-hydrate le formulaire + la référence propre depuis les valeurs enregistrées.
      const active = refreshed.find((p) => p.isDefault && p.enabled) ?? null;
      const clean = active ? formFromProvider(active) : snapshot;
      setForm(clean);
      setBaseline(clean);
      hydratedRef.current = true;
      setSaved(true);
    } catch (err: unknown) {
      if (err instanceof AiApiError && err.status === 403) {
        setError(t('llm.errorForbidden'));
      } else {
        setError(t('llm.errorGeneric'));
      }
    } finally {
      setSaving(false);
    }
  }, [saving, form, libraryId, activeProvider, t]);

  const errId = 'llm-error';

  /**
   * Bouton « Tester » en VERT (succès) : joignable ET, si un modèle a été testé, il
   * ACCEPTE les images (vision). Un modèle texte-seul (image refusée) ne verdit donc
   * PLUS le bouton — fini le « Connexion OK » trompeur.
   */
  const testOk =
    test.status === 'done' && test.reachable && (test.vision === null || test.vision.imageAccepted);

  return (
    <section
      id="admin-llm"
      role="tabpanel"
      aria-labelledby="admin-llm-heading"
      className="flex flex-col gap-8"
    >
      <div className="flex max-w-3xl flex-col gap-8">
        {/* En-tête de section (l'en-tête de page + le fil d'Ariane viennent d'AdminPanel). */}
        <div className="flex flex-col gap-1">
          <h3
            id="admin-llm-heading"
            className="flex items-center gap-2 text-base font-semibold text-fg"
          >
            <CpuChipIcon className="h-5 w-5" aria-hidden="true" />
            {t('llm.title')}
          </h3>
          <p className="text-sm text-muted">{t('llm.intro')}</p>
        </div>

        {/* Chargement des fournisseurs en échec (audit F10) : l'écran reste utilisable
            (on peut saisir et enregistrer une configuration), mais on ne laisse plus
            croire à une installation vierge. */}
        {loadError && (
          <p
            role="alert"
            className="inline-flex items-start gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-fg"
          >
            <ExclamationTriangleIcon
              className="h-5 w-5 shrink-0 text-warning-text"
              aria-hidden="true"
            />
            <span>{t('llm.loadError')}</span>
          </p>
        )}

        {/* Coupe-circuit IA global (Incrément B) : EN TÊTE de la Configuration LLM
            (foyer IA). Désactivé → masque « Tagger avec l'IA » pour admin/user et
            refuse tout nouvel enfilage côté serveur (409). */}
        <AiEnabledToggle />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onSave();
          }}
          className="flex flex-col gap-6 rounded-lg border border-border p-4"
        >
          {/* Fournisseur */}
          <div className="flex flex-col gap-1">
            <label htmlFor="llm-kind" className="text-sm font-medium text-fg">
              {t('llm.providerLabel')}
            </label>
            <select
              id="llm-kind"
              value={form.kind}
              onChange={(e) => onChangeKind(e.target.value as AiProviderKind)}
              className={selectClass}
            >
              {PROVIDER_ORDER.map((kind) => {
                const isActive = activeProvider?.kind === kind;
                return (
                  <option key={kind} value={kind}>
                    {t(PROVIDER_LABEL[kind])}
                    {isActive ? ` — ${t('llm.activeSuffix')}` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* URL de base */}
          <div className="flex flex-col gap-1">
            <label htmlFor="llm-baseurl" className="text-sm font-medium text-fg">
              {t('llm.baseUrlLabel')}
            </label>
            <input
              id="llm-baseurl"
              type="text"
              inputMode="url"
              value={form.baseUrl}
              onChange={(e) => onChangeConnField({ baseUrl: e.target.value })}
              className={inputClass}
            />
            <p className="text-xs text-muted">{t('llm.baseUrlHint')}</p>
          </div>

          {/* Modèle : saisie libre + <select> peuplé après un test réussi */}
          <div className="flex flex-col gap-1">
            <label htmlFor="llm-model" className="text-sm font-medium text-fg">
              {t('llm.modelLabel')}
            </label>
            <input
              id="llm-model"
              type="text"
              value={form.model}
              onChange={(e) => onChangeConnField({ model: e.target.value })}
              placeholder={t('llm.modelPlaceholder')}
              className={inputClass}
            />
            {/* Découverte de modèles ABOUTIE mais VIDE (audit F10) : sans ce message,
                la zone restait muette et l'utilisateur ne savait pas si le test avait
                échoué ou si le fournisseur n'expose aucun modèle. */}
            {models.length === 0 && test.status === 'done' && test.reachable && (
              <p className="mt-1 text-xs text-muted">{t('llm.modelDiscoveredEmpty')}</p>
            )}
            {models.length > 0 && (
              <div className="mt-1 flex flex-col gap-1">
                <p id="llm-models-label" className="text-xs font-medium text-muted">
                  {t('llm.modelDiscoveredLabel')} · {t('llm.modelClickToChoose')}
                </p>
                {/* Liste VISIBLE des modèles découverts : chaque modèle est un
                      bouton cliquable qui renseigne le champ « Modèle ». Le modèle
                      choisi est signalé par une coche + fond/bordure (jamais la
                      couleur seule). Hauteur bornée + défilement si nombreux. */}
                <ul
                  aria-label={t('llm.modelDiscoveredLabel')}
                  className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-md border border-border p-1"
                >
                  {models.map((m) => {
                    const selected = m.id === form.model;
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          aria-pressed={selected}
                          onClick={() => onChangeConnField({ model: m.id })}
                          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand ${
                            selected
                              ? 'border border-brand bg-brand/10 font-medium text-fg'
                              : 'border border-transparent text-fg hover:bg-surface-2'
                          }`}
                        >
                          {selected ? (
                            <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                          ) : (
                            <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                          )}
                          <span className="min-w-0 flex-1 truncate">{m.label ?? m.id}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted">
              {form.kind === 'openai_compat' ? t('llm.modelHintLmstudio') : t('llm.modelHint')}
            </p>
          </div>

          {/* Mode d'action IA */}
          <div className="flex flex-col gap-1">
            <label htmlFor="llm-actionmode" className="text-sm font-medium text-fg">
              {t('llm.actionModeLabel')}
            </label>
            <select
              id="llm-actionmode"
              value={form.actionMode}
              onChange={(e) =>
                setForm((f) => ({ ...f, actionMode: e.target.value as AiActionMode }))
              }
              className={selectClass}
            >
              {ACTION_MODE_ORDER.map((mode) => (
                <option key={mode} value={mode}>
                  {t(ACTION_MODE_LABEL[mode])}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted">{t('llm.actionModeHint')}</p>
          </div>

          {/* Plancher de capture (affiché en POURCENT ; stocké en fraction 0..1). */}
          <div className="flex flex-col gap-1">
            <label htmlFor="llm-capture-floor" className="text-sm font-medium text-fg">
              {t('llm.captureFloorLabel')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="llm-capture-floor"
                type="number"
                min={0}
                max={100}
                step={1}
                value={clampPercent(form.captureFloor * 100)}
                onChange={(e) => {
                  const pct = clampPercent(Number.parseFloat(e.target.value));
                  setForm((f) => ({ ...f, captureFloor: pct / 100 }));
                  setSaved(false);
                }}
                className="h-11 w-24 rounded-md border border-border bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              />
              <span className="text-sm text-muted" aria-hidden="true">
                %
              </span>
            </div>
            <p className="text-xs text-muted">{t('llm.captureFloorHint')}</p>
          </div>

          {/* Template de prompt d'indexation (EN ANGLAIS). Vide = prompt par défaut. */}
          <div className="flex flex-col gap-1">
            <label htmlFor="llm-prompt-template" className="text-sm font-medium text-fg">
              {t('llm.promptTemplateLabel')}
            </label>
            <textarea
              id="llm-prompt-template"
              rows={10}
              value={form.promptTemplate}
              onChange={(e) => {
                const value = e.target.value;
                setForm((f) => ({ ...f, promptTemplate: value }));
                setSaved(false);
              }}
              placeholder={DEFAULT_TAG_PROMPT_TEMPLATE}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs leading-relaxed text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, promptTemplate: DEFAULT_TAG_PROMPT_TEMPLATE }));
                  setSaved(false);
                }}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                {t('llm.promptTemplateReset')}
              </button>
              <p className="text-xs text-muted">{t('llm.promptTemplateHint')}</p>
            </div>
          </div>

          {/* Badge « Clé API détectée » (via la variable d'env, jamais la clé). */}
          <ApiKeyBadge requiresKey={requiresKey} test={test} />

          {/* Boutons : Tester la connexion + Enregistrer et activer */}
          <div className="flex flex-wrap items-center gap-3">
            {/* « Tester la connexion » : passe au VERT (succès) quand le dernier test
                a réussi (joignable). Reste cliquable pour re-tester. Jamais la couleur
                seule → icône CheckCircle + libellé « Connexion OK ». */}
            <button
              type="button"
              onClick={() => void onTest()}
              disabled={test.status === 'testing'}
              className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
                testOk
                  ? 'border-success text-success hover:bg-success/10'
                  : 'border-border text-fg hover:bg-surface-2'
              }`}
            >
              {test.status === 'testing' ? (
                <ArrowPathIcon
                  className="h-5 w-5 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : testOk ? (
                <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <SignalIcon className="h-5 w-5" aria-hidden="true" />
              )}
              {test.status === 'testing'
                ? t('llm.testing')
                : testOk
                  ? t('llm.testConnectionOk')
                  : t('llm.testConnection')}
            </button>

            {/* « Enregistrer et activer » : passe au ROUGE (bg-danger) tant qu'il y a
                des modifications non enregistrées, sinon reste en marque (bg-brand). */}
            <button
              type="submit"
              disabled={saving}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
                dirty ? 'bg-danger text-danger-fg' : 'bg-brand text-brand-fg'
              }`}
            >
              <CheckBadgeIcon className="h-5 w-5" aria-hidden="true" />
              {saving ? t('llm.saving') : t('llm.saveAndActivate')}
            </button>

            {dirty && (
              <p className="inline-flex items-center gap-1 text-sm text-danger">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                {t('llm.unsavedChanges')}
              </p>
            )}
            {saved && !dirty && (
              <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
                <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                {t('llm.saved')}
              </p>
            )}
            {error && (
              <p
                id={errId}
                role="alert"
                className="inline-flex items-center gap-1 text-sm text-danger"
              >
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                {error}
              </p>
            )}
          </div>

          {/* Résultat du test (joignable / modèles), icône + texte (jamais la couleur seule). */}
          {test.status === 'done' && <TestResult test={test} />}
        </form>

        {/* Panneau bas « Indications fournisseur ». */}
        <ProviderHints kind={form.kind} />
      </div>
    </section>
  );
}

/**
 * Bascule SUPERADMIN du coupe-circuit IA global (Incrément B). Contrôle `role=switch`
 * accessible (`aria-checked`), état + icône (jamais la couleur seule) : `SparklesIcon`
 * = IA active, `ExclamationTriangleIcon` = IA désactivée. Le PATCH met à jour le
 * contexte `aiEnabled` → l'entrée « Tagger avec l'IA » disparaît/réapparaît partout.
 */
function AiEnabledToggle(): React.JSX.Element {
  const { t } = useI18n();
  const { aiEnabled, setAiEnabled } = useAppConfig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onToggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const next = !aiEnabled;
    try {
      const config = await updateAppSettings(next);
      setAiEnabled(config.aiEnabled);
      recordAction('ai.toggle', { enabled: config.aiEnabled ? 1 : 0 });
    } catch (err: unknown) {
      setError(
        err instanceof AppConfigApiError && err.status === 403
          ? t('aiSwitch.forbidden')
          : t('aiSwitch.error'),
      );
    } finally {
      setBusy(false);
    }
  }, [busy, aiEnabled, setAiEnabled, t]);

  return (
    <section className="flex flex-col gap-2 rounded-lg border border-border p-4">
      {/* Case accessible (`role=checkbox` + `aria-checked`) : la COCHE + l'icône d'état
          portent le sens (jamais la couleur seule). Cliquer bascule le coupe-circuit. */}
      <button
        type="button"
        role="checkbox"
        aria-checked={aiEnabled}
        disabled={busy}
        onClick={() => void onToggle()}
        className="flex items-center gap-3 text-start disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <span
          aria-hidden="true"
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
            aiEnabled ? 'border-brand bg-brand text-brand-fg' : 'border-border bg-surface'
          }`}
        >
          {aiEnabled && <CheckIcon className="h-4 w-4" />}
        </span>
        <span className="inline-flex items-center gap-2">
          {aiEnabled ? (
            <SparklesIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
          ) : (
            <ExclamationTriangleIcon
              className="h-5 w-5 shrink-0 text-warning-text"
              aria-hidden="true"
            />
          )}
          <span className="text-sm font-medium text-fg">{t('aiSwitch.label')}</span>
        </span>
      </button>
      <p className="text-sm text-muted">
        {aiEnabled ? t('aiSwitch.hintEnabled') : t('aiSwitch.hintDisabled')}
      </p>
      {error && (
        <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * Badge « Clé API détectée / manquante / aucune clé requise ». Repose sur le
 * résultat du dernier test (`apiKeyPresent`) OU, à défaut de test, sur le fait que
 * le fournisseur exige une clé. Jamais la valeur de la clé — seulement la présence.
 */
function ApiKeyBadge({
  requiresKey,
  test,
}: {
  requiresKey: boolean;
  test: TestState;
}): React.JSX.Element {
  const { t } = useI18n();

  if (!requiresKey) {
    return (
      <p className="inline-flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-muted">
        <KeyIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {t('llm.apiKeyNotRequired')}
      </p>
    );
  }
  if (test.status !== 'done') {
    return (
      <p className="inline-flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-muted">
        <KeyIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {t('llm.apiKeyUnknown')}
      </p>
    );
  }
  return test.apiKeyPresent ? (
    <p className="inline-flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-fg">
      <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {t('llm.apiKeyDetected')}
    </p>
  ) : (
    <p className="inline-flex items-center gap-2 rounded-md bg-warning px-3 py-2 text-sm text-warning-fg">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {t('llm.apiKeyMissing')}
    </p>
  );
}

/** Résumé du test : joignable + nb de modèles, PUIS le double test du modèle (texte/vision). */
function TestResult({ test }: { test: Extract<TestState, { status: 'done' }> }): React.JSX.Element {
  const { t } = useI18n();
  if (!test.reachable) {
    return (
      <p role="status" className="inline-flex items-center gap-2 text-sm text-danger">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {test.error !== null
          ? `${t('llm.testUnreachable')} — ${test.error}`
          : t('llm.testUnreachable')}
      </p>
    );
  }
  return (
    <div role="status" className="flex flex-col gap-1.5 text-sm">
      {/* Étape 0 : fournisseur joignable + nb de modèles découverts. */}
      <p className="inline-flex items-center gap-2 text-fg">
        <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {t('llm.testReachable').replace('{count}', String(test.modelCount))}
      </p>
      {/* Double test du modèle : (A) répond au texte, (B) accepte l'image (vision). */}
      {test.vision === null ? (
        <p className="inline-flex items-center gap-2 text-muted">
          <SignalIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t('llm.visionNoModel')}
        </p>
      ) : (
        <>
          <VisionLine
            ok={test.vision.modelResponds}
            okLabel={t('llm.visionModelOk')}
            failLabel={t('llm.visionModelFail')}
            error={test.vision.modelError}
          />
          <VisionLine
            ok={test.vision.imageAccepted}
            okLabel={t('llm.visionImageOk')}
            failLabel={t('llm.visionImageFail')}
            error={test.vision.imageError}
          />
        </>
      )}
    </div>
  );
}

/** Une ligne de double test : coche/triangle + libellé, + message d'erreur si échec. */
function VisionLine({
  ok,
  okLabel,
  failLabel,
  error,
}: {
  ok: boolean;
  okLabel: string;
  failLabel: string;
  error: string | null;
}): React.JSX.Element {
  return (
    <p className={`inline-flex items-center gap-2 ${ok ? 'text-fg' : 'text-danger'}`}>
      {ok ? (
        <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      ) : (
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      )}
      <span className="min-w-0">
        {ok ? okLabel : failLabel}
        {!ok && error !== null && error !== '' ? ` — ${error}` : ''}
      </span>
    </p>
  );
}

/** Panneau « Indications fournisseur » : variable d'env, lien doc, note. */
function ProviderHints({ kind }: { kind: AiProviderKind }): React.JSX.Element {
  const { t } = useI18n();
  const docUrl = PROVIDER_DOC_URL[kind];
  const envName = DEFAULT_API_KEY_ENV[kind];

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <h2 className="text-base font-semibold text-fg">{t('llm.hintsTitle')}</h2>
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="font-medium text-muted">{t('llm.hintProvider')}</dt>
        <dd className="text-fg">{t(PROVIDER_LABEL[kind])}</dd>

        <dt className="font-medium text-muted">{t('llm.hintEnvVar')}</dt>
        <dd className="text-fg">
          {envName !== '' ? (
            <code className="rounded bg-surface-2 px-1.5 py-0.5">{envName}</code>
          ) : (
            t('llm.hintNoEnvVar')
          )}
        </dd>

        {docUrl !== null && (
          <>
            <dt className="font-medium text-muted">{t('llm.hintDoc')}</dt>
            <dd>
              <a
                href={docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('llm.hintDocLink')}
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              </a>
            </dd>
          </>
        )}
      </dl>
      <p className="text-sm text-muted">{t(PROVIDER_NOTE_KEY[kind])}</p>
    </section>
  );
}
